/**
 * Connected Stories Vendor
 *
 * Handles Connected Stories (HTV) specific behavior for VPAID creatives.
 * Connected Stories has two ways to access their video element:
 *
 * 1. Via window.vpaid_htv.videoTag (direct access when available)
 *    - Can be a video element like video#htv_main_video
 *    - Can be null in some cases
 *
 * 2. Via iframe#htv_iframe > contentWindow.htv_data.player.videoElement.nativeElement
 *    - Fallback when videoTag is null
 *    - Used for progress tracking
 *
 * This vendor encapsulates all Connected Stories specific logic:
 * - Detection of their player
 * - Video event synchronization at VPAID start
 * - Progress tracking from their video element
 */

const IFRAME_SELECTOR = 'iframe#htv_iframe';

export class ConnectedStoriesVendor {
  /**
   * Checks if Connected Stories player is available.
   * Returns true only if we can actually retrieve the video element.
   */
  static isAvailable(): boolean {
    return this._getVideoElement() !== null;
  }

  /**
   * Handles VPAID start for Connected Stories.
   * Synchronizes video events between the CS player and our video player.
   *
   * @param videoPlayer - Our video player element to sync events to
   * @returns true if Connected Stories is active and handled the start
   */
  static handleVpaidStart(videoPlayer: HTMLVideoElement): boolean {
    const csVideoElement = this._getVideoElement();
    if (!csVideoElement) {
      return false;
    }

    // Sync video events from Connected Stories player to our player
    this._syncVideoEvents(csVideoElement, videoPlayer);
    return true;
  }

  /**
   * Gets the video progress from Connected Stories player.
   * Returns the vendor's progress if available, otherwise the fallback.
   *
   * @param fallbackProgress - Progress from our video element
   * @returns Progress from Connected Stories player, or fallback if not available
   */
  static getProgress(fallbackProgress: number): number {
    const videoEl = this._getVideoElement();
    const currentTime = videoEl && videoEl.currentTime;

    // Use vendor's progress when it's a valid number (including 0); otherwise fall back
    if (typeof currentTime === 'number' && !Number.isNaN(currentTime)) {
      return currentTime;
    }
    return fallbackProgress;
  }

  /**
   * Gets the native video element from Connected Stories.
   * Tries two methods in this specific order (matching original behavior):
   * 1. iframe htv_data.player.videoElement.nativeElement (available before LOADED)
   * 2. window.vpaid_htv.videoTag (fallback, available in LOADED state)
   *
   * The order matters for timing: iframe is available earlier than window.vpaid_htv
   *
   * @returns The video element, or null if not available
   * @private
   */
  static _getVideoElement(): HTMLVideoElement | null {
    // Method 1: Try iframe htv_data structure first
    // This is available before VPAID LOADED state
    const iframe = (document.querySelector(
      IFRAME_SELECTOR
    ) as unknown) as HTMLIFrameElement;
    if (iframe) {
      const htvData = iframe.contentWindow && iframe.contentWindow.htv_data;
      const player = htvData && htvData.player;
      const videoEl = player && player.videoElement;

      if (videoEl && videoEl.nativeElement) {
        return videoEl.nativeElement;
      }
    }

    // Method 2: Fallback to window.vpaid_htv.videoTag
    // This is set by Connected Stories when their video is ready
    const vpaidHtv = window.vpaid_htv;
    if (vpaidHtv && vpaidHtv.videoTag) {
      // Check if videoTag is a valid video element (not an empty placeholder object)
      // HTMLVideoElement properties are not enumerable, so check for a DOM property
      const videoTag = vpaidHtv.videoTag as HTMLElement;
      const isVideoElement =
        videoTag instanceof HTMLVideoElement || videoTag.tagName === 'VIDEO';

      if (isVideoElement) {
        return vpaidHtv.videoTag;
      }
    }

    return null;
  }

  static _syncVideoEvents(
    sourceVideo: HTMLVideoElement,
    targetVideo: HTMLVideoElement
  ): void {
    const mediaEvents = [
      'play',
      'pause',
      'ended',
      'volumechange',
      'timeupdate',
      'seeking',
      'seeked',
      'waiting',
      'canplay',
      'canplaythrough',
      'loadeddata',
      'loadedmetadata',
      'error',
      'stalled',
      'playing',
      'ratechange',
      'durationchange',
      'progress',
      'suspend'
    ];

    mediaEvents.forEach(eventType => {
      sourceVideo.addEventListener(eventType, e => {
        const clonedEvent = new (e as any).constructor(eventType, {
          bubbles: e.bubbles,
          cancelable: e.cancelable,
          composed: e.composed
        });
        targetVideo.dispatchEvent(clonedEvent);
      });
    });
  }
}
